
/**
 * Module dependencies.
 */

import { Box, RawHtml, Type, color, media, normalizeNewLine, units } from '@untile/react-components';
import { CompanyPage } from 'src/types/company-page';
import { Fullpage, Section } from 'src/components/fullpage';
import { fetchCompany } from 'src/api/company/fetch-company';
import { useVerticalBarContext } from 'src/context/vertical-bar/context';
import AwardsSection from 'src/components/sections/awards-section';
import CertificationsSection from 'src/components/sections/certifications-section';
import ChartsSection from 'src/components/sections/charts-section';
import Container from 'src/components/core/layout/container';
import CycleSection from 'src/components/sections/cycle-section';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Footer from 'src/components/footer';
import ImageSection from 'src/components/sections/image-section';
import Loading from 'src/components/loading';
import Metatags from 'src/components/core/metatags';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import TextImageSection from 'src/components/sections/text-image-section';
import compact from 'lodash/compact';
import styled from 'styled-components';
import useCompany from 'src/api/company/use-company';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  data: CompanyPage
};

/**
 * `ChartsGrid` styled component.
 */

const ChartsGrid = styled.div`
  ${media.min('md')`
    display: grid;
    grid-gap: 10px;
    grid-template-areas:
      'title1   title1'
      'graphic1 graphic2'
      'info   title2';
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, max-content);
    padding-top: ${units(7)};
  `}

  canvas {
    height: auto !important;
    width: 100% !important;
  }
`;

/**
 * `StyledInfo` styled component.
 */

const StyledInfo = styled(Type.Paragraph).attrs({ size: 'small' })`
  color: ${color('grey500')};
  padding: ${units(2)} ${units(1)} 0;
  text-align: center;

  ${media.min('md')`
    grid-area: info;
    padding: ${units(1)} ${units(1)} 0;
  `}

  p {
    font-size: 8px;
    line-height: 14px;
  }
`;

/**
 * `FooterSection` styled component.
 */

const FooterSection = styled(Section)`
  padding: 0 !important;
`;

/**
 * `Company` page.
 */

const Company = (props: Props): ReactElement => {
  const { translate } = useTranslate();
  const {
    data,
    isLoading,
    isSuccess
  } = useCompany<CompanyPage>({ initialData: props?.data });

  const sectionsTitles = useMemo(() => compact([
    translate('common:verticalBar.company'),
    translate('common:verticalBar.landmarkingPartners'),
    translate('common:verticalBar.engineeringTheFuture'),
    translate('common:verticalBar.designingTheFuture'),
    translate('common:verticalBar.operationCycle'),
    translate('common:verticalBar.turnOver'),
    data?.certificationsTitle,
    data?.awardsTitle,
    translate('common:verticalBar.engineeringTheFuture')
  ]), [data?.awardsTitle, data?.certificationsTitle, translate]);

  const [visibleSection, setVisibleSection] = useState<number>(0);
  const { setTitle } = useVerticalBarContext();
  const handleAfterLoad = useCallback((_, destination) => {
    setTitle(sectionsTitles[destination.index]);
  }, [sectionsTitles, setTitle]);

  const handleOnLeave = useCallback((_, destination) => {
    setVisibleSection(destination.index);
    setTitle(sectionsTitles[destination.index]);
  }, [sectionsTitles, setTitle]);

  return (
    <>
      <Metatags
        pageTitle={translate('metatags:company.title')}
        title={translate('metatags:company.title')}
      />

      <Loading active={isLoading} />

      {isSuccess && (
        <Fullpage
          options={{
            afterLoad: handleAfterLoad,
            onLeave: handleOnLeave
          }}
        >
          {() => (
            <>
              <Section>
                <ImageSection
                  canTrigger={visibleSection === 0}
                  imageUrl={data?.image}
                  isAnimated={false}
                  isVisible={visibleSection === 0}
                  title={data?.title}
                />
              </Section>

              <Section>
                <TextImageSection
                  canTrigger={visibleSection === 1}
                  description={data?.section1Description}
                  imageUrl={data?.section1Image}
                  isAnimated={false}
                  isReversed
                  isVisible={visibleSection === 1}
                  title={data?.section1Title}
                />
              </Section>

              <Section>
                <TextImageSection
                  canTrigger={visibleSection === 2}
                  description={data?.section2Description}
                  imageUrl={data?.section2Image}
                  isAnimated={false}
                  isVisible={visibleSection === 2}
                  logoHeight={'66px'}
                  logoUrl={data?.section2ImageLogo}
                  logoWidth={'103px'}
                  title={data?.section2Title}
                />
              </Section>

              <Section>
                <TextImageSection
                  canTrigger={visibleSection === 3}
                  description={data?.section3Description}
                  imageUrl={data?.section3Image}
                  isAnimated={false}
                  isReversed
                  isVisible={visibleSection === 3}
                  logoHeight={'91px'}
                  logoUrl={data?.section3ImageLogo}
                  logoWidth={'163px'}
                  title={data?.section3Title}
                />
              </Section>

              <Section>
                <CycleSection
                  canTrigger={visibleSection === 4}
                  data={data?.companyCycle}
                  isAnimated={false}
                  isVisible={visibleSection === 4}
                  title={data?.cycleTitle}
                />
              </Section>

              <Section>
                <Container>
                  <ChartsGrid>
                    <Box
                      gridArea={'title1'}
                      marginBottom={units(6)}
                    >
                      <FadeInAnimation
                        canTrigger={visibleSection === 5}
                        isAnimated={false}
                        isVisible={visibleSection === 5}
                        options={{
                          transitionDelay: 0.5,
                          transitionTime: 1
                        }}
                      >
                        <RawHtml element={Type.H1}>
                          {normalizeNewLine(data?.graphicsTitle)}
                        </RawHtml>
                      </FadeInAnimation>
                    </Box>

                    <Box gridArea={'graphic1'}>
                      <FadeInUpAnimation
                        canTrigger={visibleSection === 5}
                        isAnimated={false}
                        isVisible={visibleSection === 5}
                        options={{
                          distance: '25vh',
                          transitionDelay: 0.75,
                          transitionTime: 1
                        }}
                      >
                        <ChartsSection
                          data={data?.companyGraphic1}
                          labelHelper={'M'}
                          xAxleLabel={translate('company:sectionGraphics.labels.year')}
                          yAxleLabel={'M€'}
                          yAxleMaxExtra={10}
                        />
                      </FadeInUpAnimation>
                    </Box>

                    {data?.graphicsInfo && (
                      <StyledInfo>
                        <FadeInAnimation
                          canTrigger={visibleSection === 5}
                          isAnimated={false}
                          isVisible={visibleSection === 5}
                          options={{
                            transitionDelay: 1.75,
                            transitionTime: 1
                          }}
                        >
                          <Type.Paragraph size={'small'}>
                            {data?.graphicsInfo}
                          </Type.Paragraph>
                        </FadeInAnimation>
                      </StyledInfo>
                    )}

                    <Box
                      gridArea={'title2'}
                      marginBottom={units(6)}
                      paddingTop={units(6)}
                      textAlignMd={'right'}
                    >
                      <FadeInAnimation
                        canTrigger={visibleSection === 5}
                        isAnimated={false}
                        isVisible={visibleSection === 5}
                        options={{
                          transitionDelay: 1.25,
                          transitionTime: 1
                        }}
                      >
                        <RawHtml element={Type.H1}>
                          {normalizeNewLine(data?.graphicsSubtitle)}
                        </RawHtml>
                      </FadeInAnimation>
                    </Box>

                    <Box gridArea={'graphic2'}>
                      <FadeInUpAnimation
                        canTrigger={visibleSection === 5}
                        isAnimated={false}
                        isVisible={visibleSection === 5}
                        options={{
                          distance: '25vh',
                          transitionDelay: 1.5,
                          transitionTime: 1
                        }}
                      >
                        <ChartsSection
                          data={data?.companyGraphic2}
                          xAxleLabel={translate('company:sectionGraphics.labels.year')}
                          yAxleLabel={translate('company:sectionGraphics.labels.worksNumber')}
                          yAxleMaxExtra={50}
                        />
                      </FadeInUpAnimation>
                    </Box>
                  </ChartsGrid>
                </Container>
              </Section>

              <Section>
                <CertificationsSection
                  canTrigger={visibleSection === 6}
                  certifications={data?.companyCertifications}
                  isAnimated={false}
                  isVisible={visibleSection === 6}
                  title={data?.certificationsTitle}
                />
              </Section>

              <Section>
                <AwardsSection
                  awards={data?.companyAwards}
                  canTrigger={visibleSection === 7}
                  isAnimated={false}
                  isVisible={visibleSection === 7}
                  title={data?.awardsTitle}
                />
              </Section>

              <Section>
                <TextImageSection
                  canTrigger={visibleSection === 8}
                  description={data?.section4Description}
                  imageUrl={data?.section4Image}
                  isAnimated={false}
                  isVisible={visibleSection === 8}
                  logoHeight={'65px'}
                  logoPosition={'bottom'}
                  logoUrl={data?.section4ImageLogo}
                  logoWidth={'232px'}
                  title={data?.section4Title}
                />
              </Section>

              <FooterSection className={'fp-auto-height'}>
                <Footer />
              </FooterSection>
            </>
          )}
        </Fullpage>
      )}
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale }) {
  const data = await fetchCompany({ locale, ssr: true });

  if (!data) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      ...data ? data : {}
    }
  };
}

/**
 * Export `Company` page.
 */

export default Company;
